import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import { queryClient } from '@/lib/queryClient'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { UserRole } from '@/types/enums'
import type { Profile } from '@/types'
import { ProtectedRoute } from '@/components/shared/ProtectedRoute'
import { AppShell } from '@/components/layout/AppShell'
import LoginPage from '@/app/auth/LoginPage'
import RegisterPage from '@/app/auth/RegisterPage'
import { Loader2 } from 'lucide-react'

/* ── Lazy-loaded pages ── */
const ClientDashboard = lazy(() => import('@/app/client/ClientDashboard'))
const ClientTickets = lazy(() => import('@/app/client/ClientTickets'))
const ClientNewTicket = lazy(() => import('@/app/client/ClientNewTicket'))
const ClientTicketDetail = lazy(() => import('@/app/client/ClientTicketDetail'))
const ClientProducts = lazy(() => import('@/app/client/ClientProducts'))
const AgentDashboard = lazy(() => import('@/app/agent/AgentDashboard'))
const AgentTicketList = lazy(() => import('@/app/agent/AgentTicketList'))
const AgentTicketDetail = lazy(() => import('@/app/client/ClientTicketDetail')) // shared component
const AgentKanban = lazy(() => import('@/app/agent/AgentKanban'))
const AdminDashboard = lazy(() => import('@/app/admin/AdminDashboard'))
const AdminProducts = lazy(() => import('@/app/admin/AdminProducts'))
const AdminSerialCodes = lazy(() => import('@/app/admin/AdminSerialCodes'))
const AdminClients = lazy(() => import('@/app/admin/AdminClients'))
const AdminAgents = lazy(() => import('@/app/admin/AdminAgents'))
const AdminTags = lazy(() => import('@/app/admin/AdminTags'))
const EnquiryPage = lazy(() => import('@/app/shared/EnquiryPage'))
const AdminEmailTemplates = lazy(() => import('@/app/admin/AdminEmailTemplates'))
const AdminAssignmentRules = lazy(() => import('@/app/admin/AdminAssignmentRules'))
const SavedRepliesPage = lazy(() => import('@/app/shared/SavedRepliesPage'))
const AdminReports = lazy(() => import('@/app/admin/AdminReports'))
const AdminRegisteredProducts = lazy(() => import('@/app/admin/AdminRegisteredProducts'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-5 h-5 animate-spin text-surface-400" />
    </div>
  )
}

function Placeholder({ title }: { title: string }) {
  return (
    <div className="text-center py-16">
      <h1 className="text-xl font-bold text-surface-900">{title}</h1>
      <p className="text-surface-500 text-sm mt-1">Coming soon</p>
    </div>
  )
}

/* ── Auth Initializer ── */
function AuthProvider({ children }: { children: React.ReactNode }) {
  const { setUser, setProfile, setLoading } = useAuthStore()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        supabase.from('profiles').select('*').eq('id', session.user.id).single()
          .then(({ data }) => { setProfile(data ? (data as Profile) : null); setLoading(false) })
      } else { setLoading(false) }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        supabase.from('profiles').select('*').eq('id', session.user.id).single()
          .then(({ data }) => { setProfile(data ? (data as Profile) : null); setLoading(false) })
      } else { setProfile(null); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [setUser, setProfile, setLoading])

  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Auth (public) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* ── Client routes ── */}
              <Route element={<ProtectedRoute allowedRoles={[UserRole.Client]}><AppShell /></ProtectedRoute>}>
                <Route path="/client/dashboard" element={<ClientDashboard />} />
                <Route path="/client/tickets" element={<ClientTickets />} />
                <Route path="/client/tickets/new" element={<ClientNewTicket />} />
                <Route path="/client/tickets/:id" element={<ClientTicketDetail />} />
                <Route path="/client/products" element={<ClientProducts />} />
                <Route path="/client/profile" element={<Placeholder title="Profile" />} />
              </Route>

              {/* ── Agent routes ── */}
              <Route element={<ProtectedRoute allowedRoles={[UserRole.Agent, UserRole.Admin]}><AppShell /></ProtectedRoute>}>
                <Route path="/agent/dashboard" element={<AgentDashboard />} />
                <Route path="/agent/tickets" element={<AgentTicketList />} />
                <Route path="/agent/tickets/kanban" element={<AgentKanban />} />
                <Route path="/agent/tickets/:id" element={<AgentTicketDetail />} />
                <Route path="/agent/enquiries" element={<EnquiryPage />} />
                <Route path="/agent/saved-replies" element={<SavedRepliesPage />} />
              </Route>

              {/* ── Admin routes ── */}
              <Route element={<ProtectedRoute allowedRoles={[UserRole.Admin]}><AppShell /></ProtectedRoute>}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/serial-codes" element={<AdminSerialCodes />} />
                <Route path="/admin/clients" element={<AdminClients />} />
                <Route path="/admin/agents" element={<AdminAgents />} />
                <Route path="/admin/tickets" element={<AgentTicketList />} />
                <Route path="/admin/enquiries" element={<EnquiryPage />} />
                <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
                <Route path="/admin/assignment-rules" element={<AdminAssignmentRules />} />
                <Route path="/admin/saved-replies" element={<SavedRepliesPage />} />
                <Route path="/admin/tags" element={<AdminTags />} />
                <Route path="/admin/reports" element={<AdminReports />} />
                <Route path="/admin/registered-products" element={<AdminRegisteredProducts />} />
              </Route>

              {/* Fallback */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors toastOptions={{ style: { fontFamily: 'var(--font-sans)' } }} />
    </QueryClientProvider>
  )
}
